// @givmo/app-charity — the console's shell: the ONE layout (CPA-WEB40 · shell contract v3).
//
// A two-pane SaaS frame on the @givmo/design-system SaaS layer, matching the company console's chrome
// so an admin moving between them does not re-learn the furniture.
//
// ══ ONE LAYOUT, CHOSEN NOWHERE (T6.1) ══════════════════════════════════════════════════════════
//
// Every authenticated page renders the same frame: the sidebar (a drawer below `lg`, the OM-13
// machinery unchanged) + the top bar (hamburger at phone · the G logomark at phone/tablet · the
// page-only breadcrumb · the cluster). The `module` layout, its Statement · Payments tab strip and
// the 100px bar token RETIRED: a module page wears the same frame, and what differs per page is the
// sidebar's CONTENT — decided by the package's ONE rule (`sidebarFor`) over this class's declarations
// (`console/console-nav.ts`) and rendered by its `OrgSidebarNav`.
//
// ══ A ROW GATES ON THE PERMISSION STRING ITS PAGE REQUIRES — NEVER A TIER (THE BUILD LAW) ════════
//
// `sidebarFor`'s only authority input is the caller's EFFECTIVE permission set off the ONE standing
// read (`GET /org/{ref}/me`) and the home app's on/off standing. A row the person may open is a real
// link; a missing string in an app they do not hold is a REQUEST row opening the one request
// dialog; a missing string in an app they hold is a statement. The Admin Console family is narrowed
// by the capabilities the pages declare (`features/org/org-pages.ts`), and the account menu's "Admin
// Console" row renders on the same settled capability the tile renders on — nothing here reads a
// tier, and nothing here reads the membership's role except to print its display label.
//
// ══ THE BAR NAMES THE APP (OM-26, AMENDING D1), THE MARK MEANS "MY LANDING" (T1.7) ══════════════
//
// The bar holds ONE word, the APP the page is in (`sidebar.app`: the module's label on every page
// that wears its frame, "Admin Console" on the `/org` family) — and the page's own word where no app
// frames the page; the page's heading names the page and the lit row the place. No trail, no link
// crumb, no organization crumb (the sidebar's chip and the account menu's identity block name it).
// The Givmo mark — the wordmark in the sidebar/drawer header, the G logomark at 22px in the
// phone/tablet bar (D5) — carries `/` as its href and computes the landing on click as an IN-APP
// entry.
//
// ══ THE CLUSTER: [gear] · [waffle] · [avatar] (OM-13; v3) ═══════════════════════════════════════
//
// Right-aligned in the top bar of every surface, with the law that an icon renders only when its
// destination is REAL. The GEAR's destination is the preferences page (T2). The WAFFLE is UNGATED:
// the popup is the person's own app list and never empty (D6). The org switcher's rows stay in the
// account menu as a second door (OM-13) and its chip in the sidebar; the two are never one control.
// Help & support, Terms and Sign out are account-menu rows. Settings is the gear AND the LAST ROW of
// the sidebar list (OM-22, reversing D-CPO-35 (1)'s foot placement) — two doors to ONE page; the
// account menu's "Settings" row (the catalog-removal request under the same word and glyph) RETIRED
// at the framework build.
//
// ══ THE ONE REQUEST DIALOG (T5.1) ══════════════════════════════════════════════════════════════
//
// Mounted once here; the popup's tiles, the sidebar's request rows and the Donations gate (through
// `RequestAccessContext`) all open it, each pre-setting Member with Owner offered. The one row that
// once opened it Owner-only — a greyed Team row — is absent for anyone who cannot use it (Org
// Machinery OM-29).
//
// ⚠ THE ROOT KEEPS `charity-app-shell` + `[--charity-app-bar-h:56px]` AND THE BAR IS A `header`: the
// statement table reads the token to keep its column header clear of the bar, and the BLOCKING e2e
// attribution spec reads the bar's geometry off `[data-testid="charity-app-shell"] header`.
//
// DATA CONTRACT: stable `data-testid`s — `charity-app-shell`, `charity-sidebar`, `charity-brand`
// (the sidebar/drawer wordmark), `charity-brand-compact` (the phone/tablet logomark),
// `charity-content`, `charity-breadcrumb-page`, `cluster-preferences`, `menu-org-*` (the Admin
// Console rows), `menu-donations`, `menu-payments`, `menu-donations-team`,
// `menu-profile`, `menu-link-*` (greyed rows), `menu-settings-page` (the LAST row of the sidebar
// list), `menu-logout`, `menu-privacy`, `menu-help`, the footer's ONE Terms link —
// `menu-charity-terms` where the served-Terms UI is compiled in, `menu-terms` where it is not;
// since CPA-WEB62 the two never render together — `account-menu-org`,
// `account-menu-terms`, `account-menu-logout` — plus the popup's
// (`app-switcher-*`, `module-square-*`) and the org switcher's (`org-switcher-*`). The drawer's
// three: `charity-nav-toggle`, `charity-nav-close`, `charity-nav-backdrop`. RETIRED with v3, not to
// be re-used: `menu-home`, `menu-grants`, `menu-payment`, `charity-breadcrumb-root`,
// `charity-module-chrome`, `charity-module-chrome-breadcrumb`, `donations-subnav`,
// `donations-nav-*`, `cluster-org-settings`, `account-menu-removal`; with CPA-WEB41, `menu-admins`
// (the legacy Admins row); with OM-17, `account-menu-settings` (the account menu's second "Settings"
// door — the sidebar's last row and the gear are the two doors now); and with OM-22, `sidebar-foot`
// (the retired foot group the Settings row used to render in). Treat a rename like an API change.

import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  AppSwitcher,
  Avatar,
  cn,
  GivmoLogomark,
  GivmoWordmark,
  Icon,
  IconButton,
  iconButtonVariants,
  FOCUS_RING,
  Menu,
  NAV_DRAWER_TOUCH_TARGET,
  Skeleton,
  navDrawerClasses,
  testIdProp,
  useNavDrawer,
} from '@givmo/design-system';
import type { LucideLikeIcon } from '@givmo/design-system';
import {
  ConsoleAssistantSlot,
  OrgSidebarNav,
  RequestAccessDialog,
  holdsAdminConsoleCapability,
  isMountedInThisBuild,
  orgLevelWord,
  readLastModule,
  settingsRowFor,
  useConsoleAssistantDock,
  sidebarFor,
  teamPendingByModule,
  type Sidebar,
} from '@givmo/org-console';
import {
  Building2,
  ChevronRight,
  FileText,
  KeyRound,
  LifeBuoy,
  LogOut,
  // Aliased: `Menu` in this file is already the design-system dropdown the account menu is built on.
  Menu as MenuGlyph,
  Settings,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { SIGN_IN_SECURITY_ID, passkeysUsableHere, useMyPasskeysOnDemand } from '@givmo/passkeys';
import { useAuth } from '../auth/auth-context';
import { useSignOut } from '../auth/use-sign-out';
import { useCharityConsole } from '../console/charity-console-context';
import { ORG_ROUTE, SETTINGS_ROUTE } from '../console/console-routes';
import { consoleDeclarations } from '../console/console-nav';
import { squaresToTiles } from '../console/console-tiles';
import {
  ADMIN_CONSOLE_LABEL,
  CLASS_DEFAULT_SLUG,
  charityConsoleTable,
  liveOrigin,
} from '../console/module-table';
import {
  settingsHref,
  useGoToSettings,
  useSettingsFrame,
  useSettingsOrigin,
} from '../console/settings-origin';
import { useSidebarFrame } from '../console/sidebar-frame';
import { useConsoleSquares, useModuleNavigate } from '../console/use-console-squares';
import { activationHandlers, useCanTurnOn } from '../console/use-turn-on-href';
import { orgNavRowId } from '../features/org/org-pages';
import { useOrgConsoleConfig } from '../features/org-console/use-org-console-config';
import { PRIVACY_URL } from '../lib/constants';
import { useActiveOrg } from '../org/active-org-context';
import { OrgSwitcher, OrgSwitcherMenuSection } from '../org/org-switcher';
import { useGoToLanding, useRememberCurrentModule } from '../router/console-landing';
import { ConsoleFrameContext } from './console-frame-context';
import { RequestAccessContext, type RequestAccessDoor } from './request-access-context';
import { MAIN_CONTENT_ID, SkipToContent } from './skip-to-content';

export interface AppShellProps {
  children: ReactNode;
}

/** Ties the hamburger's `aria-controls` to the drawer it opens — the sidebar IS the drawer. */
const SIDEBAR_ID = 'charity-sidebar';

/**
 * The phone floor for a cluster control, unwound to the desktop toolbar size at `lg`. The design
 * system's `IconButton` tops out at 36px and `Avatar size="sm"` at 32 — both right for a desktop
 * toolbar and both under the 44px Apple/WCAG touch floor for a thumb.
 */
const CLUSTER_CONTROL = 'size-44 lg:size-36';
/**
 * Rendered through `IconButton`, not as a bare trigger — a focus decision: `Menu.Trigger` adds the
 * legacy focus glow to a BARE trigger and stands down when a `render` element is supplied, so the
 * house component leaves exactly one focus treatment, the paired one.
 */
const CLUSTER_AVATAR_TRIGGER = 'size-44 rounded-full lg:size-32';

/** The title the bar shows for a path no page, module or class row names — the not-found surface. */
export const NOT_FOUND_TITLE = 'Page not found';

/**
 * The footer link recipe — the company console's, verbatim (the ONE frame): natural width, left-
 * aligned, `-mx-4` so the hover box does not shift the text. 26px tall on purpose (`py-4`): at
 * `py-2` these measured 22px, under SC 2.5.8.
 */
const FOOTER_LINK =
  'rounded-sm px-4 py-4 -mx-4 font-sans text-s12 font-w400 tracking-givmo text-text-tertiary outline-none transition-colors hover:text-text-secondary focus-visible:text-text-secondary';

/** The ordinary left-click and nothing else — a modified click keeps its native meaning. */
function isPlainClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

/**
 * The Team access row's badge is the org-wide pending count off the standing read; the package
 * badges only a MODULE's Team row (from the module's own count), so the org page's row is decorated
 * here, on the resolved sidebar, never in the declaration.
 */
export function badgeTeamAccessRow(
  sidebar: Sidebar<LucideLikeIcon>,
  pending: number,
): Sidebar<LucideLikeIcon> {
  if (pending <= 0) return sidebar;
  const rowId = orgNavRowId('team-access');
  for (const section of sidebar.sections) {
    for (const row of section.rows) {
      if (row.id === rowId) row.badge = pending;
    }
  }
  return sidebar;
}

export function AppShell({ children }: AppShellProps) {
  const { user } = useAuth();
  // The width the docked assistant panel takes beside the column, or null (`setConsoleAssistantDock`).
  const assistantDock = useConsoleAssistantDock();
  // AS-WEB11: the account menu's "Sign-in & security" row renders only where its card does — the
  // passkey surface answering here (the card's own dark gate, one shared read, kept through a failed
  // re-read and withdrawn only by the dark 404).
  // AS-WEB16: read as the person reaches for the menu (the pointer on its trigger, the trigger's own
  // focus, the menu opening), never on a page load or the window's return — that was a request per
  // page, and a 404 per page wherever passkeys are dark.
  // EP-MNT18: and only on a page host a passkey can work on (`passkeysUsableHere`: `givmo.io` or a
  // subdomain): on a raw `*.web.app` host nothing is read, so neither the row nor the card shows.
  const { passkeys: menuPasskeys, read: readMenuPasskeys } = useMyPasskeysOnDemand(
    passkeysUsableHere() ? (user?.uid ?? null) : null,
  );
  const passkeysServed = menuPasskeys !== undefined;
  const { activeMembership } = useActiveOrg();
  const { orgRef, standings, read } = useCharityConsole();
  const { open, toggle, close, drawerRef, triggerRef } = useNavDrawer();
  const squares = useConsoleSquares();
  const goInApp = useModuleNavigate();
  const goToLanding = useGoToLanding();
  const goToSettings = useGoToSettings();
  const settingsOrigin = useSettingsOrigin();
  // The frame the settings page's origin wore, and the frame THIS navigation carried (OM-18).
  const settingsFrame = useSettingsFrame();
  const sidebarFrame = useSidebarFrame();
  const canTurnOn = useCanTurnOn();
  const config = useOrgConsoleConfig();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const currentHref = useRouterState({ select: (state) => state.location.href });
  const { signOut: handleSignOut, isSigningOut } = useSignOut();
  // The module the ONE request dialog is open for, or `null` when it is closed.
  const [request, setRequest] = useState<string | null>(null);

  const accountName = user?.displayName?.trim() || null;
  const accountEmail = user?.email?.trim() || null;
  const accountPrimary = accountName ?? accountEmail;
  // The org-level role by its display LABEL — "Global Admin" for the wire's `Owner` (A6/T7.3) — and
  // nothing for anyone else (OM-96, D-CPO-77 item 7: the org-level "Member" beside an app's own level
  // read as a contradiction).
  const accountTier = activeMembership !== null ? orgLevelWord(activeMembership.role) : null;
  // Firebase identity is available immediately — don't hold the account region hostage to the
  // memberships read; the tier line simply appears when it resolves.
  const accountReady = user !== null && accountPrimary !== null;

  // ── The ONE request door ────────────────────────────────────────────────────────────────────
  //
  // Every row and every tile pre-sets Member with Owner offered. The Team row — whose string, the
  // module's approval string, is the one Owner-tier string this shell mints — renders only for a
  // holder of it (Org Machinery OM-29), so no row asks for Owner alone any more.
  const door = useMemo<RequestAccessDoor>(() => ({ open: (module) => setRequest(module) }), []);

  // ── The popup's two sections, from the ONE derivation ───────────────────────────────────────
  const { mine, more } = useMemo(
    () =>
      squaresToTiles(squares, {
        canTurnOn,
        activationHandlers: activationHandlers(),
        managementHref: ORG_ROUTE,
        standings,
        onRequestAccess: (module) => door.open(module),
      }),
    [squares, canTurnOn, standings, door],
  );

  // ── The settings page wears the sidebar of the page the gear was pressed on ─────────────────
  //
  // D-CPO-33 ("the sidebar framed by the gear's ORIGIN"): the address the gear was pressed on rides
  // the settings page's own address (`console/settings-origin.ts`, Org Machinery OM-28) and the package
  // renders that address's sidebar on the settings page, no row lit — where it gives this person a
  // place to stand; a direct load, or an origin that does not, falls to the frame rule below.
  const onSettingsPage = pathname === SETTINGS_ROUTE;

  // ── The sidebar, from the ONE rule ──────────────────────────────────────────────────────────
  //
  // An Organization-section page (Profile) — and the settings page on a direct load — keeps the
  // frame of the module it was opened from (the 2026-09-04 walkthrough ruling): the last-module
  // hint on this device first, the class default (Donations) when the hint is absent or stale. The
  // package decides whether a candidate is mounted here and openable by this person; when neither
  // is, the page keeps the Organization section alone. The hint is read, never fetched — the same
  // device value the doorway reads on a cold entry.
  const uid = user?.uid ?? null;
  const sidebar = useMemo(() => {
    // Each Team row counts what its page lists (OM-96): every app's requests for someone whose
    // app Team pages show the whole team, the app's own for anyone else.
    const pendingByModule = teamPendingByModule(
      standings,
      read.capabilities,
      read.pendingAccessRequests,
    );
    const origin = liveOrigin();
    // The MOUNTED modules (with their labels, for the reason lines) plus the Admin Console row: a
    // published-unmounted row keeps its path in the table, and the sidebar must never read a dark
    // module's address as "inside that module" — its page is the not-found surface.
    const routes = charityConsoleTable(origin).filter(
      (row) => row.adminConsole === true || isMountedInThisBuild(row),
    );
    return badgeTeamAccessRow(
      sidebarFor({
        pathname,
        routes,
        declarations: consoleDeclarations(read.capabilities),
        standings,
        permissions: read.permissions,
        origin,
        pendingByModule,
        frameModule: orgRef === null ? null : readLastModule(uid, orgRef),
        frameFallback: CLASS_DEFAULT_SLUG,
        framePath: onSettingsPage ? settingsOrigin : null,
        // A page BORROWED into another module's frame (OM-18): the frame this navigation carried —
        // a slug from the sidebar's own row, an explicit `null` from every module-switch door,
        // absent from a module's own links (the hint decides). On the settings page the gear's
        // origin state carries the frame its page wore, so the origin re-enters exactly.
        borrowedFrom: onSettingsPage ? settingsFrame : sidebarFrame,
      }),
      read.pendingAccessRequests,
    );
  }, [
    onSettingsPage,
    orgRef,
    pathname,
    read,
    settingsFrame,
    settingsOrigin,
    sidebarFrame,
    standings,
    uid,
  ]);
  // The page's own word (the document title's), and the bar's ONE word — the app the page is in,
  // the page's own word where no app frames it (Org Machinery OM-26).
  const title = sidebar.title.length > 0 ? sidebar.title : NOT_FOUND_TITLE;
  const barWord = sidebar.app ?? title;
  const showsAdminConsole = holdsAdminConsoleCapability(read.capabilities);

  // The last-module hint, written whenever the address is inside a mounted module (T1.5) — the
  // FRAME the shell rendered, which on a borrowed page is the borrower (OM-18 design 4(c)).
  useRememberCurrentModule(pathname, sidebar.frame);

  // The sidebar's own rows carry the frame they were followed in — the ONE door that does (OM-18
  // design 4(b)); the popup's tiles share `goInApp` and take its default, an explicit none.
  const onSidebarNavigate = useCallback(
    (href: string) => goInApp(href, sidebar.frame ?? null),
    [goInApp, sidebar.frame],
  );

  // ── The LAST-ROW Settings row — the second door to the ONE settings page (OM-22) ──
  //
  // Shell chrome on every page that wears the console frame, handed to `OrgSidebarNav` below as
  // `trailingRow` and drawn as the LAST ROW of the sidebar list at ordinary row spacing (Jonathan
  // 2026-09-12, reversing D-CPO-35 (1)'s foot placement: "I just want Settings to always be the
  // last page in the left panel"); the same on every module and on the Admin Console; a plain link,
  // active on the settings page and nowhere else (the exactly-one-active-row law holds with it
  // counted — `sidebarFor` lights no row there). It navigates as the gear does, carrying THIS
  // address as the page's origin (D-CPO-33) — which is why it takes `onTrailingNavigate` and never
  // the generic in-app hop. The account menu's own "Settings" row RETIRED with it
  // (`account-menu-settings`, not re-used): one word, one page, two doors — the catalog-removal
  // request is reached from the page's Organization card.
  //
  // Both doors' `href` carries the origin in the ADDRESS (Org Machinery OM-28), so a new tab, a copied
  // link or a bookmark opens the page the click does; on the settings page it is that page's own
  // address, origin and all.
  const settingsLink = onSettingsPage ? currentHref : settingsHref(pathname, sidebar.frame ?? null);
  const settingsRow = useMemo(
    () => ({
      ...settingsRowFor<LucideLikeIcon>({ pathname, href: SETTINGS_ROUTE, icon: Settings }),
      href: settingsLink,
    }),
    [pathname, settingsLink],
  );
  const onSettingsRow = useCallback(() => {
    if (onSettingsPage) return;
    goToSettings(pathname, sidebar.frame ?? null);
  }, [goToSettings, onSettingsPage, pathname, sidebar.frame]);

  useEffect(() => {
    const pageTitle = `${title} · Givmo for Charities`;
    document.title = pageTitle;
    return () => {
      if (document.title === pageTitle) document.title = 'Givmo for Charities';
    };
  }, [title]);

  const onMark = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainClick(event)) return;
    event.preventDefault();
    close();
    goToLanding();
  };

  // ── The account region ──────────────────────────────────────────────────────────────────────
  //
  // ⚠ `saas` ON EVERY ELEMENT OF THIS MENU. `Menu`'s two skins are opt-in per element; the legacy
  // item recipe carries NO `focus-visible` style at all, so an omission here is invisible in the
  // source and leaves keyboard focus invisible on the rows this menu always shows.
  const accountRegion = accountReady ? (
    <Menu.Root
      onOpenChange={(opening) => {
        if (opening) void readMenuPasskeys();
      }}
    >
      <Menu.Trigger
        render={
          <IconButton aria-label="Account menu" tone="ghost" className={CLUSTER_AVATAR_TRIGGER} />
        }
        onPointerEnter={() => void readMenuPasskeys()}
        onFocus={() => void readMenuPasskeys()}
      >
        <Avatar name={accountPrimary} src={user.photoURL ?? undefined} size="sm" />
      </Menu.Trigger>
      <Menu.Content saas side="bottom" align="end" className="min-w-[212px]">
        {/* `px-12` matches the SaaS item padding — the identity block reads as the same column. */}
        <div className="px-12 py-8">
          {accountName ? (
            <p className="truncate font-sans text-s13 font-w600 tracking-givmo text-text-strong">
              {accountName}
            </p>
          ) : null}
          {accountEmail ? (
            <p
              className={`truncate font-sans tracking-givmo ${
                accountName
                  ? 'text-s12 font-w400 text-text-tertiary'
                  : 'text-s13 font-w600 text-text-strong'
              }`}
            >
              {accountEmail}
            </p>
          ) : null}
        </div>
        <Menu.Separator />
        {/* "Switch organization" — the account menu is a second DOOR to the switcher's rows (OM-13,
            ruled 2026-08-31); the sidebar keeps its chip. Renders nothing below two organizations. */}
        <OrgSwitcherMenuSection label="Switch organization" aboveOneOnly trailingSeparator />
        {/* ⚠ ON THE SETTLED CAPABILITY, NEVER A TIER: the same rule the Admin Console tile renders
            on — appears when the standing read settles, never vanishes; a person who holds none of
            the console's pages gets NO row rather than one that bounces them. */}
        {showsAdminConsole && (
          <Menu.Item
            saas
            testId="account-menu-org"
            onClick={() => void navigate({ to: ORG_ROUTE })}
            startIcon={<Icon icon={Building2} size={16} className="text-icon" aria-hidden />}
          >
            {ADMIN_CONSOLE_LABEL}
          </Menu.Item>
        )}
        {/* AS-WEB11: the person's own Sign-in & security card, first under Settings › Your
            settings (D-AS-21's A4). A row only where the card has something to show. */}
        {passkeysServed && (
          <Menu.Item
            saas
            testId="account-menu-sign-in-security"
            onClick={() => void navigate({ to: SETTINGS_ROUTE, hash: SIGN_IN_SECURITY_ID })}
            startIcon={<Icon icon={KeyRound} size={16} className="text-icon" aria-hidden />}
          >
            Sign-in & security
          </Menu.Item>
        )}
        {/* EP-FIX7 reachability, kept: Help & support is this row — the same in-app `/support`
            destination, the same `menu-help` id. */}
        <Menu.Item
          saas
          testId="menu-help"
          onClick={() => void navigate({ to: '/support' })}
          startIcon={<Icon icon={LifeBuoy} size={16} className="text-icon" aria-hidden />}
        >
          Help & support
        </Menu.Item>
        {/* The served Charity Terms. */}
        <Menu.Item
          saas
          testId="account-menu-terms"
          onClick={() => void navigate({ to: '/terms' })}
          startIcon={<Icon icon={FileText} size={16} className="text-icon" aria-hidden />}
        >
          Terms
        </Menu.Item>
        <Menu.Item
          saas
          testId="account-menu-logout"
          disabled={isSigningOut}
          onClick={handleSignOut}
          startIcon={<Icon icon={LogOut} size={16} className="text-icon" aria-hidden />}
        >
          {isSigningOut ? 'Signing out…' : 'Sign out'}
        </Menu.Item>
      </Menu.Content>
    </Menu.Root>
  ) : (
    // Sized to the CONTROL it stands in for, so the bar does not reflow when the account resolves.
    // No `aria-label`: `Skeleton` hard-codes `aria-hidden`, so a label there is never read.
    <Skeleton className="size-44 rounded-full lg:size-32" testId="header-account-skeleton" />
  );

  // ── The top-right utility cluster — the SUITE, wherever you are ──────────────────────────────
  //
  // ⚠ AN ICON RENDERS ONLY WHERE ITS DESTINATION IS REAL. The gear's is the settings page — opened
  // with the address it was pressed on, so the page composes for THIS module and wears its sidebar
  // (D-CPO-33); pressed on the settings page itself it stays put. The waffle's popup is the person's
  // own app list and is never empty; the avatar's menu always is. `align="end"` because a popup
  // hanging from the start edge of a right-anchored trigger runs off a 390px viewport.
  //
  // The ASSISTANT SLOT leads it: the console's ONE assistant control, which renders only on a page
  // one of the console's assistant scopes covers (`features/assistant/assistant-scopes.ts`) — the
  // email editor today — and draws nothing anywhere else, so the cluster stays `[gear][waffle][avatar]`.
  const cluster = (
    <div className="flex shrink-0 items-center gap-4 lg:gap-8">
      <ConsoleAssistantSlot />
      <a
        href={settingsLink}
        onClick={(event) => {
          if (!isPlainClick(event)) return;
          event.preventDefault();
          if (onSettingsPage) return;
          goToSettings(pathname, sidebar.frame ?? null);
        }}
        aria-label="Settings"
        aria-current={onSettingsPage ? 'page' : undefined}
        className={cn(iconButtonVariants({ tone: 'ghost', size: 'md' }), CLUSTER_CONTROL)}
        {...testIdProp('cluster-preferences')}
      >
        <Icon icon={Settings} size={18} aria-hidden />
      </a>
      <AppSwitcher
        mine={mine}
        more={more}
        onNavigate={goInApp}
        align="end"
        className={CLUSTER_CONTROL}
      />
      {accountRegion}
    </div>
  );

  return (
    <RequestAccessContext value={door}>
      {/* `--charity-app-bar-h` is the ONE source of the sticky top bar's height: the bar below sizes
          itself from it, and the statement table reads it to keep its column header clear of the bar. */}
      <div
        className="flex min-h-screen bg-surface-sunken [--charity-app-bar-h:56px]"
        {...testIdProp('charity-app-shell')}
      >
        <SkipToContent />

        {/* ── Sidebar / mobile drawer ───────────────────────────────────────────────────────────
            One element, two renderings. Below `lg` it is a fixed overlay drawer, off-canvas and
            `invisible` until the hamburger opens it. From `lg` up every drawer-specific declaration
            is unwound and this is the in-flow 256px sticky column. A `<div>` with an explicit role
            rather than a `<nav>` because the open drawer is a MODAL (`role="dialog"` is not an
            ARIA-in-HTML-allowed role for `<nav>`). The class recipe is `navDrawerClasses`. ─────── */}
        <div
          ref={drawerRef}
          id={SIDEBAR_ID}
          className={navDrawerClasses(open)}
          role={open ? 'dialog' : 'navigation'}
          aria-modal={open ? true : undefined}
          aria-label={open ? 'Navigation' : 'Primary'}
          {...testIdProp('charity-sidebar')}
        >
          {/* Brand lockup — the wordmark MEANS "my landing" (T1.7): href `/`, the landing computed
              on click as an in-app entry. The phone-only close button rides in the same row. */}
          <div className="flex h-[var(--charity-app-bar-h)] items-center justify-between gap-8 px-16">
            <a
              href="/"
              onClick={onMark}
              // `min-h-44`: the drawer's wordmark is a phone control too — the tap floor.
              className={cn('flex min-h-44 items-center gap-8 rounded-md outline-none', FOCUS_RING)}
              {...testIdProp('charity-brand')}
            >
              <GivmoWordmark className="h-[22px] w-auto text-brand-500" />
            </a>
            <button
              type="button"
              onClick={close}
              aria-label="Close navigation"
              className={`-mr-12 ${NAV_DRAWER_TOUCH_TARGET}`}
              {...testIdProp('charity-nav-close')}
            >
              <Icon icon={X} size={20} aria-hidden />
            </button>
          </div>

          {/* Active-org switcher — an interactive menu for EVERY admin who has an org (D-CP-36 made
              this the only home for "Claim another organization"), and nothing at all until an org
              resolves. Its menu is portalled to <body>, which is why the shared hook decides key
              ownership. */}
          <OrgSwitcher />

          {/* The sidebar's CONTENT, from the ONE rule: a live row is a real link, a greyed row is a
              request door or a statement, never `aria-disabled`. A destination chosen closes the
              drawer, so it never covers what it navigated to.

              No `captions` here: the FRAME decides (Org Machinery OM-27) — the rule draws the group
              captions only in the frame of a module that declares more than one section, and every
              module this console mounts declares one, so the list stays captionless and gapless
              exactly as OM-21/OM-23 left it (Jonathan, 2026-09-12: "we don't really have enough
              pages here yet to justifiy having header categories"). The prop stays an override. */}
          <OrgSidebarNav
            sidebar={sidebar}
            onNavigate={onSidebarNavigate}
            onRequestAccess={(module) => door.open(module)}
            onRowActivate={close}
            trailingRow={settingsRow}
            onTrailingNavigate={onSettingsRow}
            className="flex-1 overflow-y-auto px-12 py-12"
          />

          {/* Bottom account region — IDENTITY AND SIGN-OUT ONLY (Org Machinery OM-22). Sign-out is
              always present (`menu-logout`) so the e2e/contract selector stays directly clickable,
              with the user identity inline. The Settings row is NOT here: Jonathan, 2026-09-12 — "I
              did not actually intend for that to be added to the little box with the user avatar
              where you sign out" — it is the nav's last row now. */}
          <div className="border-t border-border-subtle p-12">
            <div className="flex items-center gap-10 rounded-md p-8">
              {accountReady ? (
                <>
                  <Avatar name={accountPrimary} src={user.photoURL ?? undefined} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-sans text-s13 font-w600 tracking-givmo text-text-strong">
                      {accountPrimary}
                    </span>
                    {accountTier ? (
                      <span className="block truncate font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                        {accountTier}
                      </span>
                    ) : null}
                  </span>
                </>
              ) : (
                <div
                  className="flex min-w-0 flex-1 items-center gap-10"
                  {...testIdProp('sidebar-account-skeleton')}
                >
                  <Skeleton className="size-32 shrink-0 rounded-full" />
                  <span className="flex min-w-0 flex-1 flex-col gap-6">
                    <Skeleton className="h-12 w-3/4" />
                    <Skeleton className="h-10 w-1/2" />
                  </span>
                </div>
              )}
              <IconButton
                aria-label="Sign out"
                tone="ghost"
                // The cluster's floor, in the drawer footer: 44 on a phone, the desktop size at `lg`.
                className="size-44 lg:size-36"
                disabled={isSigningOut}
                aria-busy={isSigningOut || undefined}
                onClick={handleSignOut}
                {...testIdProp('menu-logout')}
              >
                <Icon icon={LogOut} size={18} aria-hidden />
              </IconButton>
            </div>

            {/* The company console's footer, verbatim: natural-width links, left-aligned, `gap-12`,
                and TWO of them on ONE row at every width — no `flex-wrap`.
                ★ CPA-WEB62 / D-CP-66 (Jonathan, 2026-09-12: "Drop"). The July UIX1 relabel put three
                links here — Platform Terms · Charity Terms · Privacy Policy — which wrapped to two
                rows. Jonathan has ruled the Platform Terms link OFF this surface: a charity
                administrator's terms are the Charity Terms, and the platform's own are not what
                "Terms" means in their console. So `Terms` is the in-app served Charity Terms
                (`menu-charity-terms`, the `goInApp` door). The compiled-out arm that once rendered
                the platform's `TERMS_URL` here under `menu-terms` went with its knob (CPA-WEB67);
                the pre-claim onboarding chrome keeps its own platform-terms footer. */}
            <div className="mt-4 flex items-center gap-12 px-8">
              <a
                href="/terms"
                onClick={(event) => {
                  if (!isPlainClick(event)) return;
                  event.preventDefault();
                  close();
                  goInApp('/terms');
                }}
                className={cn(FOOTER_LINK, FOCUS_RING)}
                {...testIdProp('menu-charity-terms')}
              >
                Terms
              </a>
              <span aria-hidden className="text-text-placeholder">
                ·
              </span>
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noreferrer"
                className={cn(FOOTER_LINK, FOCUS_RING)}
                {...testIdProp('menu-privacy')}
              >
                Privacy
              </a>
            </div>
          </div>
        </div>

        {/* ── Main column ──────────────────────────────────────────────────────────────────────
            `inert` while the drawer is open: everything behind a modal must be unreachable, and this
            one attribute covers the top bar AND the content without a second focus guard. */}
        <div
          className="flex min-w-0 flex-1 flex-col"
          inert={open}
          // The docked assistant's width, reserved beside the column so the page reflows instead of
          // sitting under it (`setConsoleAssistantDock`); nothing while it is closed or a sheet.
          style={assistantDock === null ? undefined : { paddingRight: assistantDock }}
        >
          {/* ⚠ THE PHONE BAR IS SIZED FOR "Admin Console" (87px), the longest word it carries (OM-26).
              Below `md` the header's gap is 8 and the separator stands down, so the word gets the
              viewport less 268px: 92px at 360, 107 at 375, 122 at 390 — measured in Chromium. With
              the separator and `gap-12` it got 68 at 360 and 83 at 375 ("Admin Cons…"). From `md`
              there is room for both; from `lg` the word stands alone here. */}
          <header className="sticky top-0 z-10 flex h-[var(--charity-app-bar-h)] shrink-0 items-center justify-between gap-8 border-b border-border-subtle bg-surface px-16 md:gap-12 lg:px-24">
            <div className="flex min-w-0 items-center gap-6">
              {/* The phone's only way into the nav. `lg:hidden` keeps it out of the flex flow on
                  desktop. */}
              <button
                ref={triggerRef}
                type="button"
                onClick={toggle}
                aria-label={open ? 'Close navigation' : 'Open navigation'}
                aria-expanded={open}
                aria-controls={SIDEBAR_ID}
                className={`-ml-12 ${NAV_DRAWER_TOUCH_TARGET}`}
                {...testIdProp('charity-nav-toggle')}
              >
                <Icon icon={MenuGlyph} size={20} aria-hidden />
              </button>
              {/* ⚠ THE G LOGOMARK AT 22px, PHONE AND TABLET ONLY (D5): the always-present Givmo mark
                  in the top left when the sidebar is a closed drawer. The desktop bar carries no
                  brand — the sidebar does. Same meaning as the wordmark: "my landing". */}
              <a
                href="/"
                onClick={onMark}
                className={cn(
                  // ⚠ A 44px HIT AREA around a 22px mark: the tap floor at phone (SC 2.5.8 and the
                  // cluster's own floor), so the logomark is as easy to hit as the hamburger beside it.
                  'flex min-h-44 min-w-44 shrink-0 items-center justify-center rounded-md outline-none lg:hidden',
                  FOCUS_RING,
                )}
                {...testIdProp('charity-brand-compact')}
              >
                <GivmoLogomark className="h-[22px] w-auto text-brand-500" />
              </a>
              {/* ⚠ A SEPARATOR AT TABLET WIDTH ONLY (`md` to `lg`), so the mark and the app's name
                  cannot fuse into a product lockup ("Givmo Donations"). Below `md` it stands down so
                  the app's word fits a 360px phone (the OM-26 review fold); from `lg` it goes with
                  the mark. */}
              <Icon
                icon={ChevronRight}
                size={14}
                className="shrink-0 text-icon-muted max-md:hidden lg:hidden"
                aria-hidden
              />
              {/* ⚠ ONE WORD, THE APP'S (OM-26, amending D1's page-only word): the module the page
                  is in, never a trail and never a link — the heading names the page and the lit
                  row the place. So it is no breadcrumb landmark and no `aria-current`: the word
                  names where the page sits, not the page. The testid keeps its name (a contract). */}
              <div className="flex min-w-0 items-center font-sans text-s13 tracking-normal">
                <span
                  className="truncate font-w600 text-text-secondary"
                  {...testIdProp('charity-breadcrumb-page')}
                >
                  {barWord}
                </span>
              </div>
            </div>
            {cluster}
          </header>

          {/* `tabIndex={-1}` is what makes the skip link move FOCUS rather than only the viewport. */}
          <main
            id={MAIN_CONTENT_ID}
            tabIndex={-1}
            className="min-w-0 flex-1 outline-none"
            {...testIdProp('charity-content')}
          >
            {/* The page reads the frame this sidebar wears (the settings page's module section —
                the OM-28 review fold), never a second derivation of it. */}
            <ConsoleFrameContext value={sidebar.frame ?? null}>{children}</ConsoleFrameContext>
          </main>
        </div>

        {/* The drawer's barrier — mounted only while open, `lg:hidden` so a mid-drawer resize into
            desktop can never leave a scrim over the portal; OUTSIDE the `inert` column so tapping it
            keeps working while the drawer is modal. `onMouseDown` prevents the scrim from ever TAKING
            focus (a press-and-drag would otherwise park the caret on it and swallow the next Escape). */}
        {open && (
          <button
            type="button"
            tabIndex={-1}
            onClick={close}
            onMouseDown={(event) => event.preventDefault()}
            aria-label="Dismiss navigation"
            className="fixed inset-0 z-40 bg-scrim lg:hidden"
            {...testIdProp('charity-nav-backdrop')}
          />
        )}

        {/* The ONE request dialog (T5.1). */}
        {config !== null && (
          <RequestAccessDialog
            config={config}
            open={request !== null}
            onOpenChange={(next) => {
              if (!next) setRequest(null);
            }}
            module={request}
            onSent={() => setRequest(null)}
          />
        )}
      </div>
    </RequestAccessContext>
  );
}
